import { fromMinorUnit, toShortName, toShortNameList } from '@shared/lib';
import type { UserActivityDto } from './schemas';
import type { ActivityActionType, UserActivity } from '../model/types';

const KNOWN_ACTIONS: ReadonlySet<ActivityActionType> = new Set([
  'created',
  'updated',
  'deleted',
  'closed',
  'reopened',
  'restored',
  'archived',
  'added',
  'removed',
]);

/** `action_type` (e.g. "CREATED") → our normalized union; unknown values become `other`. */
function toActionType(
  raw: string | null | undefined,
  referenceModule?: string,
): ActivityActionType {
  const key = (raw ?? '').toLowerCase();
  if (referenceModule === 'members' && (key === 'deleted' || key === 'removed')) {
    return 'removed';
  }
  if (key === 'unarchived' || key === 'unarchive' || key === 'restored') {
    return 'restored';
  }
  if (key === 'archived' || key === 'archive') {
    return 'archived';
  }
  return KNOWN_ACTIONS.has(key as ActivityActionType) ? (key as ActivityActionType) : 'other';
}

/** Modules whose UPDATED activities expose a "View changes" diff (mobile `shouldShowViewChanges`).
 *  Settlements are listed under every spelling the backend uses across its APIs (`settle`
 *  endpoints, `SETTLEMENT` journal rows, `user-settlements`) — an edited settlement must show the
 *  button whichever form the activity feed reports. */
const VIEW_CHANGES_MODULES: ReadonlySet<string> = new Set([
  'groups',
  'expenses',
  'wallet_deposit',
  'wallet_threshold',
  'settle',
  'settlement',
  'settlements',
]);

/** A change's before/after value is `dynamic` on the wire — coerce primitives to a display string. */
function toChangeValue(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

/**
 * Amount-bearing change rows arrive in MINOR units (a $2,411.55 payment logs "241155") — show
 * integer values of such fields in the 2dp major form used everywhere else ("2411.55"). Only a
 * plain integer is converted; a value already carrying a decimal point, any non-numeric text, or
 * an unsafely large number is shown as-is, so nothing that isn't a raw minor amount ever gets
 * re-scaled.
 */
function formatAmountField(fieldName: string, value: string): string {
  if (!/amount/i.test(fieldName)) return value;
  const trimmed = value.trim();
  if (!/^-?\d+$/.test(trimmed)) return value;
  const minor = Number(trimmed);
  if (!Number.isSafeInteger(minor)) return value;
  return (minor < 0 ? '-' : '') + fromMinorUnit(Math.abs(minor));
}

/** An ISO wire timestamp ("2026-08-27T00:00:00.000Z") or bare date ("2026-08-27"). */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;

/**
 * ISO wire timestamps display in the app's date format ("27-Aug 2026") instead of the raw
 * "2026-08-27T00:00:00.000Z" the backend logs for date changes. Calendar parts are read in UTC —
 * the wire value IS a midnight-UTC calendar date, so a local-time conversion would shift the day
 * for viewers west of UTC. Non-date values pass through untouched.
 */
function formatDateValue(value: string): string {
  const trimmed = value.trim();
  if (!ISO_DATE_RE.test(trimmed)) return value;
  const date = new Date(trimmed.length === 10 ? `${trimmed}T00:00:00Z` : trimmed);
  if (Number.isNaN(date.getTime())) return value;
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  return `${day}-${month} ${date.getUTCFullYear()}`;
}

/**
 * Placeholder keys whose value is a person's name (e.g. `deposited_user_name`, `member_names`) —
 * shown shortened ("Bella Sonia" → "Bella S", mobile's `shortName` convention). Group/expense
 * names (`group_name`, …) deliberately don't match.
 */
const PERSON_NAME_KEY = /(^|_)(user|member)_?names?$/;

/**
 * Resolve a `title_lang_path` into a display sentence by substituting each `:placeholder` with the
 * matching value from `meta.title_lang`. Tokens like `:currency_symbol:amount` sit back-to-back, so
 * we match greedily by name; an unknown token is left untouched rather than blanked. Person-name
 * values (possibly a comma-separated list of members) are shortened to "First L" for mobile parity.
 */
function resolveAction(
  path: string | null | undefined,
  values: Record<string, string | number> | null | undefined,
): string {
  if (!path) return '';
  if (!values) return path;
  return path.replace(/:([a-zA-Z_][a-zA-Z0-9_]*)/g, (match, key: string) => {
    if (!(key in values)) return match;
    const value = String(values[key]);
    return PERSON_NAME_KEY.test(key) ? toShortNameList(value) : value;
  });
}

/** Anti-corruption: user-activity DTO → domain (action text resolved, changes flattened). */
export function toUserActivity(dto: UserActivityDto): UserActivity {
  const referenceModule = dto.reference_module ?? '';
  const actionType = toActionType(dto.action_type, referenceModule);
  const changes = (dto.changes ?? [])
    .map((c) => {
      const fieldName = c.field_name ?? '';
      const display = (v: unknown) =>
        formatDateValue(formatAmountField(fieldName, toChangeValue(v)));
      return {
        fieldName,
        before: display(c.before_value),
        after: display(c.after_value),
      };
    })
    // The expense update is a full-replace PUT, so the backend can log rows for fields the save
    // re-sent unchanged (split/location/…) with identical before/after values. A row that changes
    // nothing isn't a change the user made — drop it so "View changes" shows only real edits.
    .filter((c) => c.before !== c.after);
  return {
    id: dto._id,
    time: dto.created_at ? new Date(dto.created_at) : null,
    action: resolveAction(dto.title_lang_path, dto.meta?.title_lang),
    groupName: dto.group_name ?? '',
    performedBy: toShortName(dto.performed_by_user_name ?? ''),
    actionType,
    referenceModule,
    changes,
    // Mobile `shouldShowViewChanges`: an UPDATED row with changes. The module must be eligible when
    // one is provided; feeds that omit `reference_module` (referenceModule === '') aren't excluded.
    canViewChanges:
      actionType === 'updated' &&
      changes.length > 0 &&
      (referenceModule === '' || VIEW_CHANGES_MODULES.has(referenceModule)),
  };
}
